import { runWithErrorReporting } from "$root/api/error-reporting";
import { WidgetDownloadTrigger } from "$root/components/DropdownMenuTrigger";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import { isAsOfReadable } from "$root/pages/PortfolioDetails/as-of/useAsOfSnapshot";
import { useQuery } from "@tanstack/react-query";
import { workspaceRepository } from "$root/functional-areas/workspace/repository";
import {
	type PortfolioWidgetData,
	PortfolioQueryWidgetBase,
	WidgetStatus,
	portfolioWidgetMissingDataReason,
} from "$root/pages/PortfolioDetails/PortfolioWidgetStatus";
import { type ContextContent, downloadContentDisposition, qualifier, withContext } from "$root/utils";
import { useMappedQuery } from "$root/third-party-integrations/react-query/hooks";
import { PortfolioContext } from "$root/widgets-architecture/contexts/portfolio";
import { useWidgetOptions } from "$root/widgets-architecture/layout/WidgetsMapper/context";
import { Column } from "@mdotm/shadcn/legacy/components";
import { DropdownMenu, Select } from "@mdotm/shadcn/mdotm/components";
import { useUnsafeUpdatedRef } from "@mdotm/utils/react-extra";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import WaterfallChart from "../../../ui-lib/charts/WaterfallChart";
import { horizonMap, horizonOptions } from "../const";
import { IconWalls } from "$root/components/IconWall";
import type { ReportHorizon } from "@eu.mdotm.api/sphere";
import { buildWidgetExportConfig } from "../widget-export";

/** One waterfall bar per asset-class/type pair, as `WaterfallChart` takes them. */
type AttributionSeries = Array<{ label: string; tooltipTitle: string; value: number }>;

const Attribution = (props: ContextContent<typeof PortfolioContext>) => {
	const { t } = useTranslation();
	const uuid = props.portfolio?.uuid;
	const benchmarkId = props.selectedBenchmark;
	const [horizon, setHorizon] = useState<ReportHorizon>(horizonMap.ONE_MONTH.value);

	const queryRaw = useQuery({
		...portfolioRepository.attribution.getQueryConfig(uuid, {
			horizon,
			benchmarkId,
			asOf: props.asOf,
		}),
		enabled: isAsOfReadable(props.asOf, props.asOfSnapshot),
	});
	const query = useMappedQuery(
		(realizedPerformanceAttribution): PortfolioWidgetData<AttributionSeries | undefined> => {
			if (!realizedPerformanceAttribution) {
				return {
					data: undefined,
					widgetStatus: portfolioWidgetMissingDataReason(props.portfolio, "Attribution"),
				};
			}

			if (!realizedPerformanceAttribution.current) {
				return {
					data: undefined,
					widgetStatus: portfolioWidgetMissingDataReason(props.portfolio, "Attribution"),
				};
			}

			const computedSeries = realizedPerformanceAttribution.current.map((el) => ({
				label: `<div class="h-[40px] flex flex-col text-end"><p><strong>${el.assetClass}</strong></p><p>${el.type}</p></div>`,
				tooltipTitle: `${el.assetClass} - ${el.type}`,
				value: el.value ?? 0,
			}));

			// An attribution payload that arrives with no series in it has nothing to show.
			if (computedSeries.length === 0) {
				return {
					data: undefined,
					widgetStatus: WidgetStatus.EMPTY,
				};
			}

			return {
				data: computedSeries,
				widgetStatus: WidgetStatus.READY,
			};
		},
		[props.portfolio],
		queryRaw,
	);

	// Download — mirrors the visible waterfall via the smart-workspace `investmentRealizedPerformanceAttribution`
	// data provider, exporting the currently-selected horizon.
	const exportAttribution = async (
		investmentUuid: string,
		benchmarkIdentifier: string,
		exportHorizon: ReportHorizon,
	) => {
		const config = buildWidgetExportConfig(
			"Attribution",
			{
				investmentUuid,
				benchmarkIdentifier,
				horizon: exportHorizon,
			},
			{ asOf: props.asOf },
		);
		await runWithErrorReporting(
			async () => {
				const file = await workspaceRepository.exportWorkspaceDataRaw.fetchQuery(config);
				await downloadContentDisposition(file);
			},
			{
				area: "portfolio",
				attemptedOperation: {
					message: "export performance attribution",
					payload: JSON.stringify(config),
				},
			},
		);
	};
	const exportAttributionRef = useUnsafeUpdatedRef(exportAttribution);

	const canDownload = Boolean(uuid && benchmarkId && query.data?.widgetStatus === WidgetStatus.READY);

	useWidgetOptions(
		() => ({
			tooltip: t("PORTFOLIO_ATTRIBUTION.TOOLTIP"),
			title: t("PORTFOLIO_ATTRIBUTION.TITLE"),
			actionHeader: canDownload ? (
				<DropdownMenu
					Trigger={WidgetDownloadTrigger}
					extraTriggerProps={{ "data-qualifier": qualifier.widgets.portfolioAttribution.downloadButton }}
					actions={[
						{
							icon: "xls",
							onClickAsync: async () => {
								await exportAttributionRef.current(uuid, benchmarkId!, horizon);
							},
							label: t("PORTFOLIO_ATTRIBUTION.DOWNLOAD_TITLE"),
							"data-qualifier": qualifier.widgets.portfolioAttribution.downloadItem,
						},
					]}
				/>
			) : null,
		}),
		[t, canDownload, uuid, benchmarkId, horizon, exportAttributionRef],
	);

	return (
		<PortfolioQueryWidgetBase
			query={query}
			iconWalls={{
				waitingForHistoricalData: (
					<Column>
						<div className="flex justify-end mb-3 max-w-1/6">
							<Select value={horizon} onChange={setHorizon} options={horizonOptions} />
						</div>
						<IconWalls.WaitForHistoricalPortfolioWidgetData />,
					</Column>
				),
			}}
		>
			{(realizedPerformanceAttribution) => (
				<div className="flex min-h-0 ">
					<div className="w-2/3 flex-col min-h-0">
						<div className="flex-1 flex justify-end mb-3 max-w-1/6">
							<Select value={horizon} onChange={setHorizon} options={horizonOptions} />
						</div>
						<WaterfallChart dataSeries={realizedPerformanceAttribution} height="360px" />
					</div>
					<div className="w-1/3 px-12 overflow-auto">
						<p className="mb-2">
							The <strong>allocation effect</strong> refers to the returns generated by allocating portfolio weights to
							specific asset classes. It determines whether taking active positions in a particular segment against the
							benchmark was a contributor or a detractor to the overall portfolio performance.
						</p>
						<p className="mb-2">
							The <strong>selection effect</strong> refers to the impact of the selection of specific stocks within a
							segment on the portfolio’s overall return. The weight of the segment in the portfolio determines the size
							of the effect—the larger the segment, the larger the effect is.
						</p>
						<p className="mb-2">
							The <strong>interaction effect</strong> captures the value added that is not attributable solely to the
							asset allocation and stock selection decisions. It is positive when outperformance is overweighted and
							when underperformance is underweighted.
						</p>
					</div>
				</div>
			)}
		</PortfolioQueryWidgetBase>
	);
};

export default withContext(PortfolioContext)(Attribution);
